import React, { useState } from 'react';
import { Mail, User, MessageSquare, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import SEO from '../components/SEO';
import { BreadcrumbSchema } from '../components/Schema';
import { trackConversion } from '../utils/analytics';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/contact/submit`, formData);
      toast({
        title: "Message Sent!",
        description: response.data.message || "We'll get back to you as soon as possible.",
      });
      
      // Track conversion
      trackConversion('contact', { subject: formData.subject });
      
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Contact Us - Get in Touch with TruckAccidentSupport"
        description="Contact TruckAccidentSupport.com for inquiries about truck safety resources, content suggestions, advertising opportunities, or general questions. We typically respond within 24-48 hours."
        canonical="/contact"
        keywords="contact truck safety, truck accident support contact, trucking safety inquiries"
      />
      <BreadcrumbSchema items={[
        { name: 'Home', path: '/' },
        { name: 'Contact', path: '/contact' }
      ]} />
      
      {/* Hero Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              <span className="text-gradient">Contact Us</span>
            </h1>
            <p className="text-xl text-gray-300">
              Have a question, suggestion, or advertising inquiry? We'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="section-padding bg-gray-950">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Contact Info Cards */}
              <div className="lg:col-span-1 space-y-4">
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-orange-500/10 mb-2">
                      <Mail className="h-6 w-6 text-orange-400" />
                    </div>
                    <CardTitle className="text-white">Email Us</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-400">
                      <a href="mailto:info@truckaccidentsupport.com" className="hover:text-orange-400 transition-colors">
                        info@truckaccidentsupport.com
                      </a>
                    </CardDescription>
                  </CardContent>
                </Card>

                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-orange-500/10 mb-2">
                      <MessageSquare className="h-6 w-6 text-orange-400" />
                    </div>
                    <CardTitle className="text-white">Response Time</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-400">
                      We typically respond within 24-48 hours during business days.
                    </CardDescription>
                  </CardContent>
                </Card>

                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-orange-500/10 mb-2">
                      <User className="h-6 w-6 text-orange-400" />
                    </div>
                    <CardTitle className="text-white">Inquiries Welcome</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-400">
                      General questions, content suggestions, advertising, partnerships.
                    </CardDescription>
                  </CardContent>
                </Card>
              </div>

              {/* Contact Form */}
              <div className="lg:col-span-2">
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-white text-2xl">Send Us a Message</CardTitle>
                    <CardDescription className="text-gray-400">
                      Fill out the form below and we'll respond as soon as possible.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="space-y-2">
                        <label htmlFor="name" className="text-sm font-medium text-gray-200">
                          Name *
                        </label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          placeholder="Your full name"
                          value={formData.name}
                          onChange={handleChange}
                          className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-500"
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <label htmlFor="email" className="text-sm font-medium text-gray-200">
                          Email *
                        </label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="your.email@example.com"
                          value={formData.email}
                          onChange={handleChange}
                          className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-500"
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <label htmlFor="subject" className="text-sm font-medium text-gray-200">
                          Subject *
                        </label>
                        <Input
                          id="subject"
                          name="subject"
                          type="text"
                          placeholder="What is your inquiry about?"
                          value={formData.subject}
                          onChange={handleChange}
                          className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-500"
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <label htmlFor="message" className="text-sm font-medium text-gray-200">
                          Message *
                        </label>
                        <Textarea
                          id="message"
                          name="message"
                          placeholder="Tell us more about your inquiry..."
                          value={formData.message}
                          onChange={handleChange}
                          className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 min-h-[150px]"
                          required
                        />
                      </div>

                      <Button
                        type="submit"
                        size="lg"
                        className="btn-primary text-white font-semibold w-full h-12"
                        disabled={loading}
                      >
                        {loading ? 'Sending...' : (
                          <>
                            <Send className="mr-2 h-5 w-5" />
                            Send Message
                          </>
                        )}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
